import { collection, doc, query, setDoc, where } from "firebase/firestore";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { ErrorText, FieldLabel, LoadingView, PrimaryButton, SBCard } from "@/components/ui";
import { db } from "@/firebase/app";
import { useCollection } from "@/hooks/firestoreHooks";
import {
  competitionInstanceFirestoreData,
  makeInstanceID,
  parseCompetitionTemplate,
  templateActiveFields,
  type LeagueCompetitionInstance,
} from "@/models/competition";
import { scopedMapsByDerivingAthleteUniversalFields } from "@/models/athleteUniversalFields";
import { humanActionAuditActor, recordActionAudit } from "@/services/actionAudit";
import { useActiveLeagueID } from "@/features/home/useLeagueSnapshot";
import { DynamicValuesEditor, type ScopedValueMaps } from "./DynamicValuesEditor";
import "@/features/home/home.css";
import "@/features/league/league.css";

// Port of CompetitionCreationView: pick a template, fill dynamic values,
// create a draft leagueCompetitionInstances doc.

const EMPTY_MAPS: ScopedValueMaps = { publicValues: {}, organizationValues: {}, leagueValues: {} };

export function CompetitionCreation() {
  const { currentUser } = useAuth();
  const { leagueID } = useActiveLeagueID();
  const navigate = useNavigate();

  const templatesQuery = leagueID ? query(collection(db, "leagueCompetitionTemplates"), where("leagueID", "==", leagueID)) : null;
  const templates = useCollection(templatesQuery, parseCompetitionTemplate, `templates:${leagueID}`);

  const [templateID, setTemplateID] = useState("");
  const [name, setName] = useState("");
  const [maps, setMaps] = useState<ScopedValueMaps>(EMPTY_MAPS);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const availableTemplates = useMemo(() => templates.data.filter((template) => !template.isArchived), [templates.data]);
  const selectedTemplate = availableTemplates.find((template) => template.id === templateID);
  const fields = selectedTemplate ? templateActiveFields(selectedTemplate) : [];

  const create = async () => {
    if (!leagueID || !selectedTemplate || !name.trim() || !currentUser) return;
    setIsSaving(true);
    setError(null);
    try {
      const timestamp = new Date();
      const derivedMaps = scopedMapsByDerivingAthleteUniversalFields(maps, fields, [], timestamp);
      const instance: LeagueCompetitionInstance = {
        id: makeInstanceID(leagueID),
        leagueID,
        sportID: selectedTemplate.sportID,
        templateID: selectedTemplate.id,
        templateName: selectedTemplate.name,
        templateVersion: selectedTemplate.version,
        name: name.trim(),
        status: "draft",
        publicValues: derivedMaps.publicValues,
        organizationValues: derivedMaps.organizationValues,
        leagueValues: derivedMaps.leagueValues,
        createdByUserID: currentUser.id,
        createdAt: timestamp,
        updatedAt: timestamp,
        updatedByUserID: currentUser.id,
      };
      await setDoc(doc(db, "leagueCompetitionInstances", instance.id), competitionInstanceFirestoreData(instance));
      void recordActionAudit({
        before: undefined,
        after: competitionInstanceFirestoreData(instance),
        context: {
          leagueID,
          targetCollection: "leagueCompetitionInstances",
          targetDocumentID: instance.id,
          targetDisplayName: instance.name,
          actionType: "create",
          actor: humanActionAuditActor(currentUser),
          fieldLabels: actionAuditFieldLabels(fields),
        },
      });
      navigate(`/competitions/${instance.id}`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Competition could not be created.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="list-screen league-screen">
      <div className="list-screen-head">
        <h1>New Competition</h1>
      </div>
      <SBCard className="home-card">
        {templates.loading && availableTemplates.length === 0 ? (
          <LoadingView />
        ) : (
          <>
            <FieldLabel label="Template">
              <select
                value={templateID}
                onChange={(e) => {
                  setTemplateID(e.target.value);
                  setMaps(EMPTY_MAPS);
                }}
              >
                <option value="">— Choose a template —</option>
                {availableTemplates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name} (v{template.version})
                  </option>
                ))}
              </select>
            </FieldLabel>
            <FieldLabel label="Competition name">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Spring Championship 2027" />
            </FieldLabel>
            {selectedTemplate && <DynamicValuesEditor fields={fields} maps={maps} onChange={setMaps} />}
            <PrimaryButton
              title={isSaving ? "Creating…" : "Create draft competition"}
              onClick={() => void create()}
              disabled={isSaving || !selectedTemplate || !name.trim()}
            />
            <ErrorText>{error ?? templates.error}</ErrorText>
          </>
        )}
      </SBCard>
    </div>
  );
}

function actionAuditFieldLabels(fields: ReturnType<typeof templateActiveFields>): Record<string, string> {
  return Object.fromEntries(
    fields.flatMap((field) =>
      ["publicValues", "organizationValues", "leagueValues"].map((prefix) => [`${prefix}.${field.id}`, field.label]),
    ),
  );
}
